import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Modal } from "flowbite-react";
import { Surface } from "../../atoms/Surface";
import { HiCheck } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import LoadingPage from "../../public/LoadingPage";
import {
  ProcessDataConfig,
  RegistrationType,
  RegistrationStep,
  SocialAuthResponse,
} from "./types";
import { useProcessDataCache } from "./hooks";
import { useCustomRegistrationForm } from "./hooks";
import { shownFieldErrors } from "./validation";
import { useRegistrationSubmit } from "./hooks";
import { useSocialAuth } from "./hooks";
import { useEmailAuth } from "./hooks/useEmailAuth";
import { ErrorState } from "./components";
import { RegistrationHeader } from "./components";
import { StepIndicator } from "./components";
import { Step1AuthMethod } from "./components";
import { Step2RegistrationForm } from "./components";
import { Step3CustomQuestions } from "./components";
import { generateCustomRegistrationHelmet } from "../../../utils/helmetUtils";
import { useLanguageDetection } from "../../../hooks/useLanguageDetection";
import PhoneValidationModal from "../../molecules/PhoneValidationModal";
import { useGetCustomRegisterUserProfile } from "../../../hooks/users";
import { captureUtmParams } from "./utmCapture";
import { captureBookingType, createBasicAuthFromAccessToken } from "./utils";
import { SAVE_REFUSALS } from "./registrationRefusals";
import { useRegistrationPhoneCheck } from "./hooks/useRegistrationPhoneCheck";
import { refusalMessageKey } from "../../../utils/refusalMessageKey";

const CustomRegister: React.FC = () => {
  const { processId } = useParams<{ processId: string }>();
  const { t } = useTranslation("clientView");

  const [currentStep, setCurrentStep] = useState<RegistrationStep>(1);
  const [successRegModal, setSuccessRegModal] = useState(false);
  const [type, setType] = useState<RegistrationType>("");
  const [socialResponse, setSocialResponse] =
    useState<SocialAuthResponse | null>(null);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const { processData, isLoading, isError, refetch } =
    useProcessDataCache(processId);

  const config = processData?.process_data as ProcessDataConfig | undefined;
  const [bookingTypeId] = useState(() =>
    processId ? captureBookingType(processId, window.location.search) : null
  );
  const formId: string | undefined = config?.sign_up_form_id || undefined;

  useLanguageDetection(processData);

  useEffect(() => {
    if (processId) {
      captureUtmParams(processId, window.location.search);
    }
  }, [processId]);

  const {
    control,
    formState: { errors },
    setValue,
    trigger,
    getValues,
  } = useCustomRegistrationForm(config);
  const shownErrors = shownFieldErrors(t, errors);

  useEffect(() => {
    const ownerCountryCode =
      processData?.entity_data?.owner_data?.user_country_code;
    if (ownerCountryCode && !getValues("user_country_code")) {
      setValue("user_country_code", ownerCountryCode);
    }
  }, [
    processData?.entity_data?.owner_data?.user_country_code,
    setValue,
    getValues,
  ]);

  const { mutate: fetchUserProfile } = useGetCustomRegisterUserProfile();

  useEffect(() => {
    if (!isExistingUser || !socialResponse?.user_identification) return;

    fetchUserProfile(
      {
        entity_identification: processData?.entity_data?.entity_identification,
        user_identification: socialResponse.user_identification,
        token: createBasicAuthFromAccessToken(socialResponse),
      },
      {
        onSuccess: (response: any) => {
          const profile = response?.data?.element;
          if (!profile) return;
          if (profile.user_first_name)
            setValue("user_first_name", profile.user_first_name);
          if (profile.user_last_name)
            setValue("user_last_name", profile.user_last_name);
          if (profile.user_user_name)
            setValue("user_user_name", profile.user_user_name);
          if (profile.user_phone) setValue("user_phone", profile.user_phone);
          if (profile.user_country_code)
            setValue("user_country_code", profile.user_country_code);
          if (profile.user_birth_date)
            setValue("user_birth_date", profile.user_birth_date);
        },
        onError: () => {},
      }
    );
  }, [isExistingUser, socialResponse?.user_identification]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasCustomQuestions = useMemo(() => {
    return config?.customQuestions && config.customQuestions.length > 0;
  }, [config?.customQuestions]);

  const handsOverToBooking =
    !!bookingTypeId ||
    (config?.booking?.enabled === true && !!config.booking.type_id);

  const totalSteps = hasCustomQuestions ? 3 : 2;

  const showQuestionsFirst = useMemo(() => {
    return config?.customQuestionsPosition === "first";
  }, [config?.customQuestionsPosition]);

  const getStepNumber = useCallback(
    (logicalStep: "auth" | "form" | "questions"): RegistrationStep => {
      if (!hasCustomQuestions) {
        if (logicalStep === "auth") return 1;
        if (logicalStep === "form") return 2;
        return 1;
      }

      if (showQuestionsFirst) {
        if (logicalStep === "questions") return 1;
        if (logicalStep === "auth") return 2;
        if (logicalStep === "form") return 3;
      } else {
        if (logicalStep === "auth") return 1;
        if (logicalStep === "form") return 2;
        if (logicalStep === "questions") return 3;
      }

      return 1;
    },
    [hasCustomQuestions, showQuestionsFirst]
  );

  const handleSuccess = useCallback(() => {
    setSuccessRegModal(true);
  }, []);

  const { saveUserProfile, saveCustomAnswers, isSubmitting } =
    useRegistrationSubmit({
      config,
      type,
      entityIdentification: processData?.entity_data?.entity_identification,
      socialResponse,
      onSuccess: handleSuccess,
      resolvedFormId: formId,
      bookingTypeId,
    });

  const { start: startPhoneCheck, modal: phoneCheckModal } =
    useRegistrationPhoneCheck(
      processData?.entity_data?.entity_identification || ""
    );

  const { handleLoginStart, handleReject, handleResolve } = useSocialAuth({
    entityIdentification: processData?.entity_data?.entity_identification,
    form_id: formId,
    setCurrentStep,
    setType,
    setSocialResponse,
    setEmail: (email: string) =>
      setValue("user_email", email, {
        shouldValidate: true,
        shouldDirty: true,
      }),
    getStepNumber,
    hasCustomQuestions: !!hasCustomQuestions,
    showQuestionsFirst: !!showQuestionsFirst,
    setIsExistingUser,
  });

  const { handleEmailRegister, handleEmailLogin } = useEmailAuth({
    entityIdentification: processData?.entity_data?.entity_identification,
    form_id: formId,
    setCurrentStep,
    setType,
    setSocialResponse,
    setEmail: (email: string) =>
      setValue("user_email", email, {
        shouldValidate: true,
        shouldDirty: true,
      }),
    getStepNumber,
    requirePassword: config?.requirePassword || false,
  });

  const handleEmailClick = useCallback(() => {
    const nextStep = getStepNumber("form");
    setCurrentStep(nextStep);
    setType("email");
  }, [getStepNumber]);

  const handleQuestionsSubmit = useCallback(
    async (e?: React.BaseSyntheticEvent) => {
      e?.preventDefault();

      if (showQuestionsFirst) {
        const questionFields =
          config?.customQuestions.map((_, index) => `question_${index}`) || [];
        const isValid = await trigger(questionFields);
        if (isValid) {
          const authStep = getStepNumber("auth");
          setCurrentStep(authStep);
        }
      } else {
        const questionFields =
          config?.customQuestions.map((_, index) => `question_${index}`) || [];
        const isValid =
          questionFields.length > 0 ? await trigger(questionFields) : true;
        if (isValid) {
          const formData = getValues();
          if (socialResponse?.user_email && !formData.user_email) {
            formData.user_email = socialResponse.user_email;
          }
          try {
            await saveCustomAnswers(formData);
          } catch (error: any) {
            console.error("Error saving custom answers:", error);
            toast.error(
              t(
                refusalMessageKey(
                  error,
                  SAVE_REFUSALS,
                  "customRegistration.page.toast.registrationFailed"
                )
              )
            );
          }
        }
      }
    },
    [
      config?.customQuestions,
      trigger,
      getStepNumber,
      showQuestionsFirst,
      setCurrentStep,
      getValues,
      saveCustomAnswers,
      socialResponse,
      t,
    ]
  );

  const handleFormSubmit = useCallback(
    async (e?: React.BaseSyntheticEvent) => {
      e?.preventDefault();

      const fieldsToValidate = [
        !socialResponse ? "user_email" : null,
        config?.requirePassword && type === "email" ? "user_password" : null,
        config?.fields?.user_first_name?.visible ? "user_first_name" : null,
        config?.fields?.user_last_name?.visible ? "user_last_name" : null,
        config?.fields?.user_user_name?.visible ? "user_user_name" : null,
        config?.fields?.user_phone?.visible ? "user_phone" : null,
        config?.fields?.user_country_code?.visible ? "user_country_code" : null,
        config?.fields?.user_birth_date?.visible ? "user_birth_date" : null,
      ].filter(Boolean) as string[];

      const isValid =
        fieldsToValidate.length === 0 ? true : await trigger(fieldsToValidate);

      if (isValid) {
        const formData = getValues();
        if (socialResponse?.user_email && !formData.user_email) {
          formData.user_email = socialResponse.user_email;
        }

        try {
          await saveUserProfile(formData);

          const continueRegistrationFlow = async () => {
            if (hasCustomQuestions && !showQuestionsFirst) {
              const questionsStep = getStepNumber("questions");
              setCurrentStep(questionsStep);
            } else {
              await saveCustomAnswers(formData);
            }
          };

          if (
            config?.fields?.user_phone?.requireVerification &&
            socialResponse?.user_identification &&
            socialResponse?.access_token
          ) {
            const phoneNumber = formData.user_phone;

            if (phoneNumber) {
              startPhoneCheck(
                {
                  userId: socialResponse.user_identification,
                  accessToken: socialResponse.access_token,
                  phoneNumber,
                },
                continueRegistrationFlow
              );
              return;
            }
          }

          await continueRegistrationFlow();
        } catch (error: any) {
          console.error("Error saving user profile:", error);
          toast.error(
            t(
              refusalMessageKey(
                error,
                SAVE_REFUSALS,
                "customRegistration.page.toast.registrationFailed"
              )
            )
          );
        }
      }
    },
    [
      saveUserProfile,
      saveCustomAnswers,
      trigger,
      config,
      type,
      socialResponse,
      hasCustomQuestions,
      showQuestionsFirst,
      getStepNumber,
      setCurrentStep,
      getValues,
      startPhoneCheck,
      t,
    ]
  );

  const handleBackFromAuth = useCallback(() => {
    const questionsStep = getStepNumber("questions");
    setCurrentStep(questionsStep);
  }, [getStepNumber]);

  const handleBackFromQuestions = useCallback(() => {
    const formStep = getStepNumber("form");
    setCurrentStep(formStep);
  }, [getStepNumber]);

  const handleBackFromForm = useCallback(() => {
    const authStep = getStepNumber("auth");
    setCurrentStep(authStep);
  }, [getStepNumber]);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isError) {
    return <ErrorState onRetry={refetch} />;
  }

  const renderCurrentStep = () => {
    if (!config) return null;

    const isQuestionsStep = currentStep === getStepNumber("questions");
    const isAuthStep = currentStep === getStepNumber("auth");
    const isFormStep = currentStep === getStepNumber("form");

    if (isQuestionsStep && hasCustomQuestions) {
      return (
        <Step3CustomQuestions
          config={config}
          control={control}
          errors={shownErrors}
          isSubmitting={isSubmitting}
          onBack={showQuestionsFirst ? undefined : handleBackFromQuestions}
          onSubmit={handleQuestionsSubmit}
          isLastStep={!showQuestionsFirst && !handsOverToBooking}
        />
      );
    }

    if (isAuthStep) {
      return (
        <Step1AuthMethod
          onEmailClick={handleEmailClick}
          onLoginStart={handleLoginStart}
          onResolve={handleResolve}
          onReject={handleReject}
          isDisabled={isSubmitting}
          onBack={
            showQuestionsFirst && hasCustomQuestions
              ? handleBackFromAuth
              : undefined
          }
          requirePassword={config?.requirePassword}
          entityIdentification={processData?.entity_data?.entity_identification}
          onEmailRegister={handleEmailRegister}
          onEmailLogin={handleEmailLogin}
          onExistingUser={() => setIsExistingUser(true)}
        />
      );
    }

    if (isFormStep) {
      return (
        <Step2RegistrationForm
          config={config}
          control={control}
          errors={shownErrors}
          type={type}
          isSubmitting={isSubmitting}
          onBack={socialResponse ? undefined : handleBackFromForm}
          onSubmit={handleFormSubmit}
          isLastStep={showQuestionsFirst && !handsOverToBooking}
          hasExistingAuth={!!socialResponse}
          ownerCountryCode={
            processData?.entity_data?.owner_data?.user_country_code
          }
        />
      );
    }

    return null;
  };

  return (
    <>
      {generateCustomRegistrationHelmet({
        processData,
        processId,
      })}

      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <RegistrationHeader
            logo={processData?.entity_data?.entity_logo}
            entityName={processData?.entity_data?.entity_name}
            entityDescription={processData?.entity_data?.entity_description}
            formDescription={config?.formDescription}
          />

          <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />

          <Surface>{renderCurrentStep()}</Surface>
        </div>
      </div>

      <Modal
        show={successRegModal}
        size="md"
        onClose={() => setSuccessRegModal(false)}
        popup
      >
        <Modal.Body className="p-4 flex flex-col justify-start items-center">
          <HiCheck size={24} className="mx-auto text-green-400" />
          <h4 className="mt-2 text-gray-900 text-sm font-medium font-figtree leading-tight">
            {t("register.form.success")}
          </h4>
        </Modal.Body>
      </Modal>

      <PhoneValidationModal namespace="clientView" {...phoneCheckModal} />
    </>
  );
};

export default CustomRegister;
